import { useEffect, useRef, useState } from 'react';
import { HiXMark } from 'react-icons/hi2';
import { FcFlashOn } from 'react-icons/fc';
import { FaCheck, FaRegCopy } from 'react-icons/fa';

import {
  SHOW_BANNER_CONDITION,
  TIME_LEFT,
  SHOW_CLOSE_ICON,
  // BANNER_TIMER_STORAGE_KEY,
} from 'constants/topBannerConfig';
import { PRICING_SECTION, PRICING_PAGE } from 'constants/pages';

import { useShowBannerConditions } from 'hooks/useShowBanner';
import useCurrentSaleConfig from 'hooks/useCurrentSaleConfig';
import { useActiveParityOffer } from 'context/ParityOfferContext';
import { useGeo } from 'context/GeoContext';
import CountryFlag from 'components/CountryFlag';
import { getParityOfferCopy, getParityOfferMode } from 'utils/shared/parityOffers';
import {
  PARITY_CHECKOUT_CLICKED,
  PARITY_CODE_COPIED,
  PARITY_OFFER_CHECKED,
} from 'services/client/analytics/events';
import { trackEvent } from 'services/client/analytics';
import { copyText } from 'utils/client/copyText';
// import useBrowserStorage from 'hooks/useBrowserStorage';

export default function TopBanner({
  showCloseIcon = SHOW_CLOSE_ICON,
  shouldShowOnScroll,
}: {
  showCloseIcon?: boolean;
  shouldShowOnScroll?: boolean;
}) {
  // Get current sale configuration - automatically detects active sale based on current date
  const saleConfig = useCurrentSaleConfig();
  const FINAL_TEXT = saleConfig.dealText;
  const FINAL_DISCOUNT_TEXT = saleConfig.discountText;
  const FINAL_BUTTON_TEXT = saleConfig.buttonText;
  const FINAL_TARGET_DATE = saleConfig.targetDate;
  const FINAL_TIME_DELAY = saleConfig.timeDelay;
  const FINAL_SHOW_ON_SCROLL = saleConfig.showOnScroll;
  // A regional offer replaces the sale outright — it is forever, the sale is one-time.
  const parityOffer = useActiveParityOffer();
  const parityCopy = parityOffer && getParityOfferCopy(parityOffer);
  // Flash removal: while the geo lookup is in flight, render nothing rather than show the
  // sale and swap to the regional offer a beat later. Only 'custom' mode can swap, so other
  // modes (off / paritydeals rollback) never wait.
  const { status: geoStatus } = useGeo();
  const awaitingParityLookup = getParityOfferMode() === 'custom' && geoStatus === 'loading';

  const showBanner = useShowBannerConditions({
    // The regional offer is not an urgency play, so it skips the sale's delay and scroll gate.
    // The subscriber check inside the hook still applies.
    timeDelay: parityOffer ? 0 : FINAL_TIME_DELAY,
    additionalCondition: SHOW_BANNER_CONDITION,
    showOnScroll: parityOffer ? false : shouldShowOnScroll ?? FINAL_SHOW_ON_SCROLL,
  });

  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [copied, setCopied] = useState(false);
  const initialTimeLeft =
    FINAL_TARGET_DATE && new Date(FINAL_TARGET_DATE).getTime() > new Date().getTime()
      ? Math.floor((new Date(FINAL_TARGET_DATE).getTime() - new Date().getTime()) / 1000)
      : TIME_LEFT;
  const [timeLeft, setTimeLeft] = useState(initialTimeLeft);
  // const [timeLeft, setTimeLeft] = useBrowserStorage<number>(
  //   BANNER_TIMER_STORAGE_KEY,
  //   TIME_LEFT,
  //   'localStorage',
  // );

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
  };

  const formatTime = (seconds: number) => {
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    const secs = seconds % 60;
    return (
      <div className="flex items-center gap-x-2">
        <span className="hidden items-center gap-2 px-1 text-xs uppercase tracking-wide text-white md:flex">
          Offer expires in
        </span>
        <span>{days}d</span>:<span>{hours}h</span>:<span>{minutes}m</span>:<span>{secs}s</span>
      </div>
    );
  };

  // A page left open across a sale boundary (Black Friday at midnight, 40% becoming 30%) gets the
  // new sale's deadline: the countdown restarts when the sale changes, instead of freezing at zero
  // or running out the previous sale's clock under the new sale's name. Seconds round up, so zero
  // arrives only once the deadline has passed and that render already reads the next sale.
  const saleType = saleConfig.saleType;

  useEffect(() => {
    // The regional offer never expires, so it runs no countdown.
    if (showBanner && !parityOffer) {
      if (FINAL_TARGET_DATE) {
        setTimeLeft(
          Math.max(0, Math.ceil((new Date(FINAL_TARGET_DATE).getTime() - Date.now()) / 1000)),
        );
      }
      const intervalId = setInterval(() => {
        if (FINAL_TARGET_DATE) {
          const now = new Date();
          const targetDate = new Date(FINAL_TARGET_DATE);
          const differenceInSeconds = Math.ceil((targetDate.getTime() - now.getTime()) / 1000);

          setTimeLeft(() => {
            if (differenceInSeconds <= 0) {
              clearInterval(intervalId);
              return 0;
            }
            return differenceInSeconds;
          });
        } else {
          setTimeLeft((prevTime: number) => {
            if (prevTime <= 0) {
              clearInterval(intervalId);
              return 0;
            }
            return prevTime - 1;
          });
        }
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [showBanner, parityOffer, saleType, setTimeLeft]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  // The bar swaps from the sale code to the regional code when the lookup resolves; a copy inside
  // that window must not keep announcing "copied" against the code that replaced it.
  useEffect(() => setCopied(false), [parityOffer?.couponCode]);

  // Impression signal: fire once when the regional bar is actually on screen for an eligible
  // non-subscriber. It fires from the rendered banner, NOT the app-wide lookup, so the paid
  // dashboard (where this bar never shows) can't inflate it.
  const impressionTracked = useRef(false);
  useEffect(() => {
    if (parityOffer && showBanner && isBannerVisible && !impressionTracked.current) {
      impressionTracked.current = true;
      trackEvent(PARITY_OFFER_CHECKED, {
        eligible: true,
        countryCode: parityOffer.countryCode,
        tierId: parityOffer.tierId,
        percentOff: parityOffer.percentOff,
        actionType: 'impression',
        surface: 'top_banner',
        route: typeof window === 'undefined' ? '' : window.location.pathname,
      });
    }
  }, [parityOffer, showBanner, isBannerVisible]);

  if (!showBanner || !isBannerVisible) return null;
  if (awaitingParityLookup) return null;

  if (parityOffer && parityCopy) {
    const parityAnalytics = (actionType: string) => ({
      countryCode: parityOffer.countryCode,
      tierId: parityOffer.tierId,
      percentOff: parityOffer.percentOff,
      actionType,
      route: typeof window === 'undefined' ? '' : window.location.pathname,
    });

    const handleCopyParityCode = () => {
      copyText(parityOffer.couponCode)
        .then(() => {
          setCopied(true);
          trackEvent(PARITY_CODE_COPIED, parityAnalytics('copy_code'));
        })
        .catch(() => setCopied(false));
    };

    // flex-wrap keeps every element visible down to 320px instead of clipping the CTA.
    // green-800/700 rather than the brand greens: white on #25cf51 is 2.1:1, failing WCAG AA.
    // This pair puts every string on the bar at 5.0:1 or better.
    return (
      <div className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 bg-gradient-to-r from-green-800 via-green-700 to-green-800 px-2 py-2 text-xs font-medium shadow-lg shadow-emerald-900/20 sm:gap-x-4 sm:px-4 sm:text-sm">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <CountryFlag
            code={parityOffer.countryCode}
            className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover shadow-sm ring-1 ring-white/30 sm:h-4 sm:w-6"
          />
          <span className="font-bold uppercase tracking-wider text-white">
            {parityCopy.headline}
          </span>
        </div>
        <span className="whitespace-nowrap rounded-full bg-black/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white">
          {parityCopy.badge}
        </span>
        <span className="hidden text-white lg:inline">{parityCopy.detail}</span>
        <div className="flex items-center gap-1.5 rounded-full border border-white/20 bg-black/25 py-0.5 pl-2 pr-1 backdrop-blur-sm sm:gap-2 sm:pl-3">
          <span className="font-mono font-bold tracking-[0.12em] text-white">
            {parityOffer.couponCode}
          </span>
          <button
            type="button"
            onClick={handleCopyParityCode}
            aria-label={`Copy coupon code ${parityOffer.couponCode}`}
            className="rounded-full p-1.5 text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-white"
          >
            {copied ? (
              <FaCheck aria-hidden="true" className="h-3 w-3" />
            ) : (
              <FaRegCopy aria-hidden="true" className="h-3 w-3" />
            )}
          </button>
        </div>
        <a
          href={`${PRICING_PAGE}#${PRICING_SECTION}`}
          onClick={() => trackEvent(PARITY_CHECKOUT_CLICKED, parityAnalytics('view_plans'))}
          className="whitespace-nowrap rounded-full bg-palette-black-1 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-white hover:text-palette-black-1 sm:px-3"
        >
          {parityCopy.cta}
        </a>
        <span className="sr-only" aria-live="polite">
          {copied ? `Coupon code ${parityOffer.couponCode} copied` : ''}
        </span>
        {showCloseIcon && (
          <div className="absolute bottom-0 right-1 top-0 my-auto flex items-center sm:right-2">
            <button
              type="button"
              className="-m-2 p-2 focus-visible:outline-offset-[-4px] sm:-m-3 sm:p-3"
              onClick={handleCloseBanner}
            >
              <span className="sr-only">Dismiss</span>
              <HiXMark aria-hidden="true" className="h-5 w-5 text-white sm:h-6 sm:w-6" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-50 flex items-center justify-center gap-1.5 bg-gradient-to-r from-brand-primary via-indigo-600 to-brand-primary px-2 py-2 text-xs font-medium shadow-lg shadow-blue-900/20 sm:gap-4 sm:px-4 sm:text-sm">
      <div className="flex animate-pulse items-center gap-0.5 sm:gap-1">
        <FcFlashOn className="h-5 w-5 sm:h-6 sm:w-6" />
        <span className="font-bold uppercase tracking-wider text-white"> {FINAL_TEXT}</span>
      </div>
      <span className="hidden sm:inline">{FINAL_DISCOUNT_TEXT}</span>
      <div className="flex items-center gap-1 rounded-full border border-white/10 bg-black/20 px-2 py-0.5 backdrop-blur-sm sm:gap-2 sm:px-3 sm:py-1">
        <div className="flex gap-1 font-mono font-bold text-white">
          <span>{formatTime(timeLeft)}</span>
        </div>
      </div>
      <a
        href={`${PRICING_PAGE}#${PRICING_SECTION}`}
        className="rounded-full bg-brand-green-secondary px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white shadow-[0_0_10px_rgba(190,242,100,0.3)] transition-colors hover:bg-white hover:text-brand-green-secondary sm:px-3"
      >
        {FINAL_BUTTON_TEXT}
      </a>
      {showCloseIcon && (
        <div className="absolute bottom-0 right-1 top-0 my-auto flex items-center sm:right-2">
          <button
            type="button"
            className="-m-2 p-2 focus-visible:outline-offset-[-4px] sm:-m-3 sm:p-3"
            onClick={handleCloseBanner}
          >
            <span className="sr-only">Dismiss</span>
            <HiXMark aria-hidden="true" className="h-5 w-5 text-white sm:h-6 sm:w-6" />
          </button>
        </div>
      )}
    </div>
  );
}
